import assert from 'assert';
import React, {useMemo} from 'react';

import {makeReactContext} from 'lib/makeReactContext';
import {TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {TableModelContext} from 'notebook/tables/store/contexts/TableModelContext';
import {
  AxisCellModel,
  createAxisCellModelSelector,
  makeStandaloneAxisCellModel,
} from 'notebook/tables/store/models/AxisCellModel';
import {createTableEditableStore, TableEditableStore} from 'notebook/tables/store/TableStore';

/**
 * Reads a slice of the {@link AxisCellModel} for the axis cell being rendered. Axis cells exist in
 * two flavors, so the accessor is supplied by the provider rather than by the consuming hook:
 * inside a table it subscribes to the table store, and standalone it reads a constant model.
 */
type UseAxisCellModelSlice = <T>(subSelector: (axisCellModel: AxisCellModel) => T) => T;

interface AxisCellContextInterface {
  useAxisCellModelSlice: UseAxisCellModelSlice;
}

export const AxisCellModelContext = makeReactContext<AxisCellContextInterface>(() => {
  assert(false, 'AxisCellModelContext must be used within an AxisCellModelProvider');
}, 'AxisCellModelContext');

function makeStoreBackedSlice(
  useTableEditableStore: ReturnType<typeof createTableEditableStore>,
  axisCellModelSelector: (store: TableEditableStore) => AxisCellModel
): UseAxisCellModelSlice {
  return function useAxisCellModelSlice<T>(subSelector: (axisCellModel: AxisCellModel) => T): T {
    return useTableEditableStore((store) => subSelector(axisCellModelSelector(store)));
  };
}

function makeConstantSlice(axisCellModel: AxisCellModel): UseAxisCellModelSlice {
  return function useAxisCellModelSlice<T>(subSelector: (axisCellModel: AxisCellModel) => T): T {
    return subSelector(axisCellModel);
  };
}

/**
 * Provides the {@link AxisCellModel} for an axis cell rendered within a table, derived from the
 * table store so that the cell re-renders only when the slice it reads changes.
 */
export function AxisCellModelProvider({
  dim,
  index,
  children,
}: {
  dim: TableDimension;
  index: number;
  children: React.ReactNode;
}): JSX.Element {
  const {tableModelSelector, useTableEditableStore} = TableModelContext.useContext();
  const useAxisCellModelSlice = useMemo(
    () =>
      makeStoreBackedSlice(
        useTableEditableStore,
        createAxisCellModelSelector(tableModelSelector, dim, index)
      ),
    [tableModelSelector, useTableEditableStore, dim, index]
  );
  const contextValue = useMemo(() => ({useAxisCellModelSlice}), [useAxisCellModelSlice]);

  return <AxisCellModelContext.Provider value={contextValue}>{children}</AxisCellModelContext.Provider>;
}

/**
 * Provides a constant {@link AxisCellModel} for an axis cell rendered outside of a table store
 * (for example `SimpleTableAxisCell`), where there is nothing to subscribe to.
 */
export function StandaloneAxisCellModelProvider({
  dim,
  index,
  children,
}: {
  dim: TableDimension;
  index: number;
  children: React.ReactNode;
}): JSX.Element {
  const useAxisCellModelSlice = useMemo(
    () => makeConstantSlice(makeStandaloneAxisCellModel(dim, index)),
    [dim, index]
  );
  const contextValue = useMemo(() => ({useAxisCellModelSlice}), [useAxisCellModelSlice]);

  return <AxisCellModelContext.Provider value={contextValue}>{children}</AxisCellModelContext.Provider>;
}
